/* eslint-disable react/prop-types */
import moment from 'moment'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import { injectIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import Select from 'react-select'
import * as dates from '../../../_augmentt/ducks/dates'
import { datePickerStyle } from '../../../_augmentt/utils/styles'

const startOfDay = { hour: 0, minute: 0, second: 0, millisecond: 0 }
const endOfDay = { hour: 23, minute: 59, second: 59, millisecond: 999 }

/** Align initial React state with the first `dateRanges` preset so bootstrap-daterangepicker never mounts on the wrong range (preset highlight vs label mismatch). */
function getInitialRangeFromProps(dateRanges) {
  if (dateRanges) {
    const firstPair = Object.values(dateRanges)[0]
    if (firstPair?.[0] && firstPair?.[1]) {
      return {
        start: moment(firstPair[0]).set(startOfDay),
        end: moment(firstPair[1]).set(endOfDay),
      }
    }
  }
  return {
    start: moment().subtract(1, 'month').set(startOfDay),
    end: moment().set(endOfDay),
  }
}

function DatePicker(props) {
  const { dateRanges, showCustomRangeLabel = true } = props

  const dispatch = useDispatch()
  const didInitFromDateRangesRef = useRef(false)

  // Single first-paint snapshot: one `getInitialRangeFromProps` call; shared `start`/`end` refs across state.
  const initialRange = useMemo(() => getInitialRangeFromProps(dateRanges), []) // eslint-disable-line react-hooks/exhaustive-deps -- mount snapshot of `dateRanges`

  const [startDate, setStartDate] = useState(initialRange.start)
  const [endDate, setEndDate] = useState(initialRange.end)
  const [selectedDates, setSelectedDates] = useState([
    {
      label: `${initialRange.start.format('MMMM DD, YYYY')} - ${initialRange.end.format('MMMM DD, YYYY')}`.toString(),
      value: { start: initialRange.start, end: initialRange.end },
    },
  ])

  // Handles date change
  const handleDateChange = useCallback(
    (start, end) => {
      const nextStart = moment(start).set(startOfDay)
      const nextEnd = moment(end).set(endOfDay)
      setStartDate(nextStart)
      setEndDate(nextEnd)
      setSelectedDates([
        {
          label: `${moment(start).format('MMMM DD, YYYY')} - ${moment(end).format('MMMM DD, YYYY')}`.toString(),
          value: { start: nextStart, end: nextEnd },
        },
      ])

      dispatch(
        dates.actions.setDateRange({
          startDate: `${moment(start).set(startOfDay).format('YYYY-MM-DDT00:00:00.000')}Z`,
          endDate: `${moment(end).set(endOfDay).format('YYYY-MM-DDT23:59:59.999')}Z`,
        }),
      )
    },
    [dispatch],
  )

  // When `dateRanges` is provided, sync Redux once. Initial UI already matches the first preset
  // (see `getInitialRangeFromProps`); avoid calling `handleDateChange` here so daterangepicker
  // is not initialized on default month then corrected after mount (preset highlight drift).
  useLayoutEffect(() => {
    if (!dateRanges || didInitFromDateRangesRef.current) {
      return
    }
    const firstPair = Object.values(dateRanges)[0]
    if (!firstPair?.[0] || !firstPair?.[1]) {
      return
    }
    didInitFromDateRangesRef.current = true
    dispatch(
      dates.actions.setDateRange({
        startDate: `${moment(firstPair[0]).set(startOfDay).format('YYYY-MM-DDT00:00:00.000')}Z`,
        endDate: `${moment(firstPair[1]).set(endOfDay).format('YYYY-MM-DDT23:59:59.999')}Z`,
      }),
    )
  }, [dateRanges, dispatch])

  // Determine date ranges to display in the left hand side of the date range picker
  const dateRangesForDatePicker = dateRanges || {
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, 'days').set(startOfDay), moment().subtract(1, 'days').set(endOfDay)],
    'Last 7 Days': [moment().subtract(6, 'days').set(startOfDay), moment().set(endOfDay)],
    'Last 30 Days': [moment().subtract(29, 'days').set(startOfDay), moment().set(endOfDay)],
    'This Month': [moment().startOf('month').set(startOfDay), moment().endOf('month').set(endOfDay)],
    'Last Month': [
      moment().subtract(1, 'month').set(startOfDay).startOf('month').set(endOfDay),
      moment().subtract(1, 'month').set(startOfDay).endOf('month').set(endOfDay),
    ],
  }

  // Initial Redux sync when not using a custom `dateRanges` prop (custom ranges run via useLayoutEffect above)
  useEffect(() => {
    if (dateRanges) {
      return
    }
    if (startDate && endDate) {
      dispatch(
        dates.actions.setDateRange({
          startDate: `${moment(startDate).set(startOfDay).format('YYYY-MM-DDT00:00:00.000')}Z`,
          endDate: `${moment(endDate).set(endOfDay).format('YYYY-MM-DDT23:59:59.999')}Z`,
        }),
      )
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DateRangePicker
      initialSettings={{
        showCustomRangeLabel,
        opens: 'left',
        drops: 'down',
        startDate,
        endDate,
        minDate: moment().subtract(4, 'month').set(startOfDay),
        maxDate: moment().set(endOfDay),
        ranges: dateRangesForDatePicker,
        // bootstrap-daterangepicker defaults to btn-default (removed in BS5)
        cancelButtonClasses: 'btn-secondary',
      }}
      onCallback={handleDateChange}
    >
      <Button variant="link" id="daterange" style={{ padding: 0 }}>
        <Select
          name="daterange"
          menuIsOpen={false}
          styles={datePickerStyle}
          defaultValue={selectedDates[0]}
          value={selectedDates[0]}
          isSearchable={false}
          openMenuOnClick={false}
        />
      </Button>
    </DateRangePicker>
  )
}

export default injectIntl(DatePicker)
